/**
 * TableSkeleton Component
 *
 * Displays a loading skeleton while a markdown table is being streamed.
 * Shows a gentle shimmer effect over a simple 3x3 grid to indicate table content is loading.
 *
 * Uses React.memo to prevent re-renders during streaming animation.
 */

import React from 'react';

interface TableSkeletonProps {
  rows?: number;
  columns?: number;
}

// Stable widths for skeleton cells (avoids Math.random() on every render)
const CELL_WIDTHS = ['75%', '85%', '65%', '90%', '70%', '80%', '60%', '95%', '72%'];

function TableSkeletonComponent({ rows = 3, columns = 3 }: TableSkeletonProps) {
  return (
    <div className="my-2 overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {Array.from({ length: columns }).map((_, colIndex) => (
                <th
                  key={`skeleton-header-${colIndex}`}
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                >
                  <div className="h-4 w-20 animate-pulse rounded bg-gray-300"></div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {Array.from({ length: rows }).map((_, rowIndex) => (
              <tr key={`skeleton-row-${rowIndex}`}>
                {Array.from({ length: columns }).map((_, colIndex) => {
                  // Use stable width based on cell position
                  const widthIndex = (rowIndex * columns + colIndex) % CELL_WIDTHS.length;
                  return (
                    <td
                      key={`skeleton-cell-${rowIndex}-${colIndex}`}
                      className="whitespace-nowrap px-6 py-4"
                    >
                      <div
                        className="h-4 animate-pulse rounded bg-gray-200"
                        style={{ width: CELL_WIDTHS[widthIndex] }}
                      ></div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Memoize to prevent re-renders during streaming character updates
const TableSkeleton = React.memo(TableSkeletonComponent);

export default TableSkeleton;
